import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { of, startWith, switchMap, tap } from 'rxjs';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { QaiLoadingComponent, QaiToggleSwitchComponent } from '@qai/ui';
import { AuthorReactiveFormService } from 'src/app/pages/self-serve/simulation-builder/services/author-reactive-form.service';
import { SelfAuthoringPersonaFormService } from 'src/app/pages/self-serve/simulation-builder/services/self-authoring-persona-form.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { ScoringRefinementService } from 'src/app/services/self-serve/scoring-refinement.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';

@Component({
  selector: 'screen-share-section',
  standalone: true,
  templateUrl: './screen-share-section.component.html',
  styleUrls: ['./screen-share-section.component.scss'],
  imports: [CommonModule, TranslocoModule, QaiToggleSwitchComponent, QaiLoadingComponent],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class ScreenShareSectionComponent {
  readonly personaId = input('');
  readonly savingPersona = input(false);
  readonly simulationUuidInput = input('', { alias: 'simulationUuid' });
  readonly moduleUuidInput = input('', { alias: 'moduleUuid' });
  readonly isSelfAuthoring = input(false);
  readonly conversePromptManualOverride = input(false);

  generalGroup!: FormGroup;

  simulationUuid: string = '';
  moduleUuid: string = '';

  checked: boolean = false;
  readOnly: boolean = false;
  private _hasPersona: boolean = false;

  private readonly route = inject(ActivatedRoute);
  private readonly authorService = inject(AuthorService);
  private readonly authorReactiveFormService = inject(AuthorReactiveFormService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly selfAuthoringPersonaFormService = inject(SelfAuthoringPersonaFormService);
  private readonly scoringRefinementService = inject(ScoringRefinementService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      this.simulationUuidInput();
      if (!this.simulationUuid) {
        this.checked = false;
      }
    });
  }

  ngOnInit(): void {
    this.simulationUuid = this.route.snapshot.paramMap.get('uuid') ?? '';
    if (!this.simulationUuid) return;

    this.readOnly = this.authorReactiveFormService.isReadOnly(
      this.simulationUuid
    );

    this.authorReactiveFormService
      .getForm(this.simulationUuid)
      .pipe(
        tap((form) => {
          this.moduleUuid =
            form?.get(SimulationConstants.MODULE_UUID_PATH)?.value ?? '';
          this.generalGroup = form?.get(
            SimulationConstants.GENERAL_SECTION
          ) as FormGroup;

          this.checked = !!this.screenshareEnabledControl?.value;

          this.authorReactiveFormService.setReadOnlyMode(
            this.simulationUuid,
            this.screenshareEnabledControl
          );
        }),
        switchMap(() => {
          const personaIdControl = this.callPersonaGroup?.get('personaId');
          return personaIdControl
            ? personaIdControl.valueChanges.pipe(startWith(personaIdControl.value))
            : of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((personaId) => {
        this._hasPersona = !!personaId;
      });
  }

  get hasPersona(): boolean {
    return this._hasPersona || !!this.personaId();
  }

  /** True while the persona is being created (createPersona in flight). */
  get isCreatingPersona(): boolean {
    if (this.hasPersona) return false;

    return this.simulationUuid
      ? this.selfAuthoringPersonaFormService.isSaving(this.simulationUuid)()
      : this.savingPersona();
  }

  get toggleDisabled(): boolean {
    return this.readOnly || !this.hasPersona || this.conversePromptManualOverride();
  }

  get toggleOn(): boolean {
    return this.checked && this.hasPersona;
  }

  get statusLabelKey(): string {
    return this.toggleOn ? 'SCREEN_SHARE_STATUS_ON' : 'SCREEN_SHARE_STATUS_OFF';
  }

  onToggleInputChange(event: Event): void {
    this.onToggle((event.target as HTMLInputElement).checked);
  }

  onToggle(next: boolean): void {
    const simulationUuid = this.targetSimulationUuid;
    if (this.toggleDisabled || !simulationUuid) return;

    const previous = this.checked;

    this.setEnabled(next);

    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.GENERAL_SCREEN_SHARE,
      {
        sim_id: simulationUuid,
        enabled: next,
      }
    );

    this.authorService
      .updateSimulation({
        uuid: simulationUuid,
        existingModuleUuid: this.targetModuleUuid,
        screenshareEnabled: next,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.isSelfAuthoring()) {
            this.scoringRefinementService.notifySaved(simulationUuid);
          }
        },
        error: () => this.setEnabled(previous),
      });
  }

  private setEnabled(value: boolean): void {
    this.screenshareEnabledControl?.setValue(value, { emitEvent: false });
    this.checked = value;
  }

  private get targetSimulationUuid(): string {
    return this.simulationUuid || this.simulationUuidInput();
  }

  private get targetModuleUuid(): string {
    return this.moduleUuid || this.moduleUuidInput();
  }

  private get screenshareEnabledControl(): AbstractControl | null {
    return this.generalGroup?.get('screenshareEnabled') ?? null;
  }

  private get callPersonaGroup(): AbstractControl | null {
    return this.generalGroup?.get(SimulationConstants.CALL_PERSONA_SECTION) ?? null;
  }
}
